import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PrimaryButton } from "./common";
import { useLang } from "../i18n";
import { pc, inr, purchase } from "../pricing";
import { payWithRazorpay } from "../lib/razorpay";
import { ddmmyyyy } from "../lib/format";
import { Sparkles, ShieldCheck, Zap, Check, Star, TrendingUp, FileText, Download, CalendarClock, Flame } from "lucide-react";

// Shared: a full-screen upsell must always have a visible dismiss ("Not now"), never only an X.
function DismissRow({ lang, onClose, testId = "not-now-btn" }) {
  return (
    <button onClick={onClose} data-testid={testId} className="w-full text-center text-sm font-semibold text-slate-500 py-2">
      {pc(lang, "not_now")}
    </button>
  );
}

// A. Plans
export function PlansSheet({ open, onOpenChange, config, status, onChanged }) {
  const { lang } = useLang();
  if (!config) return null;
  const current = status?.plan || "free";
  const choose = async (id) => {
    try {
      await payWithRazorpay(`plan_${id}`);
      await onChanged?.();
      onOpenChange(false);
    } catch (e) {
      if (e?.message !== "cancelled") alert("Payment could not be completed. Please try again.");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><Sparkles className="w-5 h-5 text-terracotta" /> {pc(lang, "plan_title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {config.plans.map((p) => (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-xl border p-3.5 ${p.popular ? "border-terracotta bg-terracotta-light" : "border-slate-200"}`}>
              <div className="flex items-center justify-between">
                <div className="font-head font-bold text-slate-900 flex items-center gap-1.5">{p.name}{p.popular && <Star className="w-3.5 h-3.5 fill-amberw text-amberw" />}</div>
                <div className="font-mono font-bold text-slate-900">{p.price === 0 ? pc(lang, "free_word") : inr(p.price)}<span className="text-xs text-slate-400 font-body">{p.price ? pc(lang, "per_month") : ""}</span></div>
              </div>
              <ul className="mt-2 space-y-1">{p.features.map((f) => <li key={f} className="text-xs text-slate-600 flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-trust shrink-0 mt-0.5" />{f}</li>)}</ul>
              {(() => {
                if (current === p.id) return <div className="mt-3 text-xs font-semibold text-trust">{pc(lang, "plan_current")}</div>;
                if (p.id === "free") return null;
                return <button onClick={() => choose(p.id)} data-testid={`choose-plan-${p.id}`} className="gs-tap w-full mt-3 rounded-xl bg-terracotta text-white font-semibold text-sm">{pc(lang, "plan_choose", { name: p.name })}</button>;
              })()}
            </div>
          ))}
        </div>
        <DismissRow lang={lang} onClose={() => onOpenChange(false)} testId="plans-stay-free-btn" />
      </DialogContent>
    </Dialog>
  );
}

export function UpgradeBanner({ onOpen }) {
  const { lang } = useLang();
  return (
    <button onClick={onOpen} data-testid="upgrade-banner" className="w-full rounded-xl bg-gradient-to-r from-slate2 to-[#0F172A] text-white p-3.5 flex items-center gap-3 text-left active:scale-[0.99] transition">
      <div className="w-9 h-9 rounded-lg bg-terracotta flex items-center justify-center"><Zap className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0"><div className="font-head font-semibold text-sm">{pc(lang, "plan_title")}</div></div>
      <span className="text-xs font-semibold text-terracotta-light bg-white/10 px-2.5 py-1 rounded-full whitespace-nowrap">{pc(lang, "plan_cta")}</span>
    </button>
  );
}

export function FeatureGate({ open, onOpenChange, feature, onSeePlans }) {
  const { lang } = useLang();
  const msg = feature === "police_filing" ? pc(lang, "plan_gate_police") : pc(lang, "plan_gate_report");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-sm rounded-2xl">
        <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><Sparkles className="w-5 h-5 text-terracotta" /> {pc(lang, "plan_title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{msg}</p>
        <PrimaryButton onClick={() => { onOpenChange(false); onSeePlans(); }} data-testid="gate-see-plans-btn">{pc(lang, "plan_cta")}</PrimaryButton>
        <DismissRow lang={lang} onClose={() => onOpenChange(false)} testId="gate-not-now-btn" />
      </DialogContent>
    </Dialog>
  );
}

// B. Payout choice — free option first, equal weight
export function PayoutChoice({ amount, onStandard, onInstant, busy }) {
  const { lang } = useLang();
  const dim = busy ? "opacity-50 pointer-events-none" : "";
  return (
    <div className="space-y-2" data-testid="payout-choice">
      <p className="text-xs font-semibold text-slate-500">{pc(lang, "payout_choose_hint")}</p>
      <button onClick={onStandard} disabled={busy} data-testid="payout-standard-btn" className={`w-full gs-card p-3.5 text-left border border-slate-200 active:scale-[0.99] ${dim}`}>
        <div className="font-semibold text-slate-800 text-sm">{pc(lang, "payout_standard")}</div>
      </button>
      <button onClick={onInstant} disabled={busy} data-testid="payout-instant-btn" className={`w-full gs-card p-3.5 text-left border border-terracotta bg-terracotta-light active:scale-[0.99] ${dim}`}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-slate-900 text-sm">{pc(lang, "payout_instant_title")}</div>
          <span className="font-mono font-bold text-terracotta">{inr(amount)}</span>
        </div>
      </button>
    </div>
  );
}

// B2. Revenue snapshot — a small "value you're getting" card for owners
const VALUE_STATS = [
  { key: "plan", labelKey: "value_plan", icon: Star },
  { key: "boosts", labelKey: "value_boosts", icon: TrendingUp },
  { key: "instant", labelKey: "value_instant", icon: Zap },
  { key: "services", labelKey: "value_services", icon: ShieldCheck },
];
const PLAN_NAMES = { free: "Free", basic: "Basic", pro: "Pro", portfolio: "Portfolio" };

export function RevenueSnapshot({ data, onUpgrade }) {
  const { lang } = useLang();
  if (!data) return null;
  const values = {
    plan: PLAN_NAMES[data.plan] || data.plan,
    boosts: data.featured_count ?? 0,
    instant: data.instant_payouts ?? 0,
    services: data.services_count ?? 0,
  };
  return (
    <div className="gs-card p-4" data-testid="revenue-snapshot">
      <div className="flex items-center gap-2 font-head font-semibold text-slate-800 text-sm mb-3"><Sparkles className="w-4 h-4 text-terracotta" /> {pc(lang, "value_title")}</div>
      <div className="grid grid-cols-2 gap-2.5">
        {VALUE_STATS.map((s) => (
          <div key={s.key} className="rounded-xl bg-slate-50 p-3" data-testid={`value-stat-${s.key}`}>
            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-semibold"><s.icon className="w-3.5 h-3.5" /> {pc(lang, s.labelKey)}</div>
            <div className="font-head font-bold text-slate-900 text-lg mt-0.5">{values[s.key]}</div>
          </div>
        ))}
      </div>
      {Number(data.invested) > 0 && <p className="text-xs text-slate-400 mt-2.5 text-center">{pc(lang, "value_invested", { amount: Number(data.invested).toLocaleString("en-IN") })}</p>}
      {data.instant_payout_log?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid="instant-payout-ledger">
          <div className="text-xs font-semibold text-slate-500 mb-1.5">{pc(lang, "payout_ledger_title")}</div>
          <div className="space-y-1.5">
            {data.instant_payout_log.map((p, i) => (
              <div key={p.created_at || i} data-testid={`payout-ledger-row-${i}`} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-slate-600"><Zap className="w-3 h-3 text-terracotta" /> {ddmmyyyy(p.created_at)}</span>
                <span className="text-slate-700 font-mono">{inr(p.amount)}</span>
                <span className="text-trust font-semibold">{pc(lang, "payout_time_saved")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.plan === "free" && (
        <button onClick={onUpgrade} data-testid="revenue-snapshot-upgrade" className="gs-tap w-full mt-3 rounded-xl bg-terracotta text-white font-semibold text-sm">{pc(lang, "value_nudge_free")}</button>
      )}
    </div>
  );
}

// B5. Boost auto-nudge — prompt to lock in a tenant when a boost gets hot
export const BOOST_HOT_VIEWS = 20;

export function BoostHotNudge({ items, onCreate }) {
  const { lang } = useLang();
  const hot = (items || []).filter((l) => (l.extra_views || 0) >= BOOST_HOT_VIEWS);
  if (hot.length === 0) return null;
  const top = [...hot].sort((a, b) => b.extra_views - a.extra_views)[0];
  return (
    <div className="gs-card p-4 border border-terracotta/40 bg-terracotta-light" data-testid="boost-hot-nudge">
      <div className="flex items-center gap-2 font-head font-semibold text-slate-900 text-sm mb-1"><Flame className="w-4 h-4 text-terracotta" /> {pc(lang, "boost_hot_title")}</div>
      <p className="text-xs text-slate-600 mb-3">{pc(lang, "boost_hot_line", { title: top.title, n: top.extra_views })}</p>
      <button onClick={() => onCreate(top.id)} data-testid="boost-hot-cta" className="gs-tap w-full rounded-xl bg-terracotta text-white font-semibold text-sm py-2.5">{pc(lang, "boost_hot_cta")}</button>
    </div>
  );
}

// B3. Boost insights — extra views/enquiries each boosted listing earned
export function BoostInsights({ items }) {
  const { lang } = useLang();
  if (!items || items.length === 0) return null;
  return (
    <div className="gs-card p-4" data-testid="boost-insights">
      <div className="flex items-center gap-2 font-head font-semibold text-slate-800 text-sm mb-3"><TrendingUp className="w-4 h-4 text-amberw" /> {pc(lang, "boost_insights_title")}</div>
      <div className="space-y-2.5">
        {items.map((l) => (
          <div key={l.id} data-testid={`boost-insight-${l.id}`} className="rounded-xl bg-amberw-light/60 p-3">
            <div className="font-semibold text-slate-800 text-sm truncate">{l.title}</div>
            <div className="flex gap-4 mt-1 text-xs font-semibold">
              <span className="text-trust">{pc(lang, "boost_extra_views", { n: l.extra_views })}</span>
              <span className="text-terracotta">{pc(lang, "boost_extra_enquiries", { n: l.extra_enquiries })}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// B4. Renewal nudge — agreements ending soon
export function RenewalNudge({ items, onRenew, onRelist }) {
  const { lang } = useLang();
  if (!items || items.length === 0) return null;
  return (
    <div className="gs-card p-4 border border-amberw/40 bg-amberw-light" data-testid="renewal-nudge">
      <div className="flex items-center gap-2 font-head font-semibold text-slate-800 text-sm mb-2"><CalendarClock className="w-4 h-4 text-amberw" /> {pc(lang, "renewal_title")}</div>
      <div className="space-y-2.5">
        {items.map((a) => (
          <div key={a.id} data-testid={`renewal-item-${a.id}`} className="rounded-xl bg-white p-3">
            <div className="font-semibold text-slate-800 text-sm truncate">{a.property_address}</div>
            <div className="text-xs text-slate-500 mt-0.5">{pc(lang, "renewal_days_left", { n: a.days_left })}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => onRenew(a.id)} data-testid={`renewal-renew-${a.id}`} className="gs-tap flex-1 rounded-xl bg-terracotta text-white font-semibold text-xs py-2">{pc(lang, "renewal_renew")}</button>
              <button onClick={onRelist} data-testid={`renewal-relist-${a.id}`} className="gs-tap flex-1 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs py-2">{pc(lang, "renewal_relist")}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// C. Service bundle inline card
export function ServiceCard({ titleKey, price, anchor, onPaid, onDIY, testId = "service-card" }) {
  const { lang } = useLang();
  return (
    <div className="gs-card p-4 border border-terracotta/30" data-testid={testId}>
      <div className="flex items-center gap-2 font-head font-semibold text-slate-800 text-sm mb-1"><Sparkles className="w-4 h-4 text-terracotta" /> {pc(lang, titleKey)}</div>
      {anchor && <p className="text-xs text-slate-400 mb-2">{pc(lang, "svc_anchor", { who: anchor.who, low: anchor.low.toLocaleString("en-IN"), high: anchor.high.toLocaleString("en-IN") })}</p>}
      <button onClick={onPaid} data-testid={`${testId}-paid`} className="gs-tap w-full rounded-xl bg-terracotta text-white font-semibold text-sm flex items-center justify-center gap-2">{pc(lang, "svc_handle")} · {inr(price)}</button>
      <button onClick={onDIY} data-testid={`${testId}-diy`} className="w-full text-center text-sm font-semibold text-slate-500 py-2 mt-1">{pc(lang, "svc_diy")}</button>
    </div>
  );
}

// D. Featured listing — only when eligible (5+ days, low views); pulls real data
export function FeaturedCard({ listingId, api, config }) {
  const { lang } = useLang();
  const [elig, setElig] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [boosted, setBoosted] = useState(false);
  useEffect(() => { api.get(`/listings/${listingId}/featured-eligibility`).then((r) => setElig(r.data)).catch(() => {}); }, [api, listingId]);
  if (dismissed || boosted || !elig?.eligible) return null;
  const price = config?.prices?.featured_listing?.amount || 199;
  const boost = async () => {
    try {
      await payWithRazorpay("featured_listing", { listing_id: listingId });
      setBoosted(true);
    } catch (e) {
      if (e?.message !== "cancelled") alert("Payment could not be completed. Please try again.");
    }
  };
  return (
    <div className="gs-card p-3.5 border border-amberw/40 bg-amberw-light" data-testid={`featured-card-${listingId}`}>
      <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm"><TrendingUp className="w-4 h-4 text-amberw" /> {pc(lang, "featured_cta")}</div>
      <p className="text-xs text-slate-500 my-1.5">{pc(lang, "featured_line", { views: elig.views, days: elig.days_live })}</p>
      <div className="flex gap-2">
        <button onClick={boost} data-testid={`boost-btn-${listingId}`} className="gs-tap flex-1 rounded-xl bg-terracotta text-white font-semibold text-sm">{pc(lang, "featured_cta")} · {inr(price)}</button>
        <button onClick={() => setDismissed(true)} data-testid={`featured-dismiss-${listingId}`} className="gs-tap px-4 rounded-xl border border-slate-200 text-slate-500 text-sm font-semibold">{pc(lang, "not_now")}</button>
      </div>
    </div>
  );
}

// E. Matching charge (only after tenancy confirmed)
export function MatchingChargeSheet({ open, onOpenChange, rent, config, onPaid }) {
  const { lang } = useLang();
  const amount = config?.prices?.matching_fee?.amount || 1499;
  const pay = async (agreementId) => { await purchase("matching_fee", { agreement_id: agreementId, amount }); onPaid?.(); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-sm rounded-2xl">
        <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><Check className="w-5 h-5 text-trust" /> {pc(lang, "match_confirmed_title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700">{pc(lang, "match_line", { amount: amount.toLocaleString("en-IN") })}</p>
        {rent > 0 && <p className="text-xs text-slate-400 mt-1">{pc(lang, "match_anchor", { rent: rent.toLocaleString("en-IN") })}</p>}
        <PrimaryButton onClick={() => pay(open)} data-testid="matching-pay-btn" className="mt-3">{pc(lang, "match_pay", { amount: amount.toLocaleString("en-IN") })}</PrimaryButton>
        <DismissRow lang={lang} onClose={() => onOpenChange(false)} testId="matching-not-now-btn" />
      </DialogContent>
    </Dialog>
  );
}

// F. AI cap
export function AiCapSheet({ open, onOpenChange, onUpgrade }) {
  const { lang } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-sm rounded-2xl">
        <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><Sparkles className="w-5 h-5 text-terracotta" /> {pc(lang, "ai_cap_title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{pc(lang, "ai_cap_line")}</p>
        <PrimaryButton onClick={() => { onOpenChange(false); onUpgrade(); }} data-testid="ai-cap-upgrade-btn">{pc(lang, "ai_cap_cta")}</PrimaryButton>
        <DismissRow lang={lang} onClose={() => onOpenChange(false)} testId="ai-cap-not-now-btn" />
      </DialogContent>
    </Dialog>
  );
}

// G. Insurance (move-in / renewal)
export function InsuranceSheet({ open, onOpenChange, kind, config }) {
  const { lang } = useLang();
  const isDeposit = kind === "deposit";
  const p = config?.prices?.[isDeposit ? "deposit_insurance" : "rent_insurance"] || {};
  const add = async () => { await purchase(isDeposit ? "deposit_insurance" : "rent_insurance", { amount: p.amount }); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-sm rounded-2xl">
        <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-trust" /> {pc(lang, isDeposit ? "ins_deposit_title" : "ins_income_title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700">{pc(lang, isDeposit ? "ins_deposit_scenario" : "ins_income_scenario", { cover: (p.cover || 0).toLocaleString("en-IN") })}</p>
        <PrimaryButton onClick={add} data-testid="insurance-add-btn" className="mt-3">{pc(lang, "ins_cta")} · {inr(p.amount)}</PrimaryButton>
        <DismissRow lang={lang} onClose={() => onOpenChange(false)} testId="insurance-not-now-btn" />
      </DialogContent>
    </Dialog>
  );
}

// H. Tax pack seasonal banner
export function TaxPackBanner({ active, price, onGet }) {
  const { lang } = useLang();
  if (!active) return null;
  return (
    <div className="gs-card p-3.5 border border-trust/40 bg-trust-light flex items-center gap-3" data-testid="tax-pack-banner">
      <div className="w-9 h-9 rounded-lg bg-trust flex items-center justify-center text-white"><FileText className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0"><div className="font-head font-semibold text-sm text-slate-800">{pc(lang, "tax_title")}</div><div className="text-xs text-slate-500">{pc(lang, "tax_line")}</div></div>
      <button onClick={onGet} data-testid="tax-pack-btn" className="gs-tap px-3 rounded-xl bg-trust text-white text-sm font-semibold flex items-center gap-1"><Download className="w-4 h-4" /> {inr(price || 299)}</button>
    </div>
  );
}
